import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Building, RefreshCw, FileText, DollarSign, CreditCard, Eye, XCircle,
  Building2, Check, AlertCircle, Truck
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const GestionPagos = () => {
  const [loading, setLoading] = useState(true);
  const [proveedoresConSaldo, setProveedoresConSaldo] = useState([]);
  const [ordenesPago, setOrdenesPago] = useState([]);
  const [cuentasTesoreria, setCuentasTesoreria] = useState([]);
  const [activeTab, setActiveTab] = useState("pendientes");
  
  const [modalOP, setModalOP] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [opDetalle, setOpDetalle] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [comprobantesPendientes, setComprobantesPendientes] = useState([]);
  const [comprobantesSeleccionados, setComprobantesSeleccionados] = useState([]);
  
  const [formOP, setFormOP] = useState({
    fecha: new Date().toISOString().split('T')[0],
    medio_pago: "TRANSFERENCIA",
    cuenta_tesoreria_id: "",
    referencia: "",
    observaciones: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [proveedoresRes, opRes, cuentasRes] = await Promise.all([
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/proveedores-con-saldo"),
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/ordenes-pago"),
        axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/cuentas-tesoreria")
      ]);
      
      setProveedoresConSaldo(proveedoresRes.data);
      setOrdenesPago(opRes.data);
      setCuentasTesoreria(cuentasRes.data);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerOP = async (id) => {
    try {
      const res = await axios.get(API_URL + "/api/erp/empresas/" + TENANT_ID + "/ordenes-pago/" + id);
      setOpDetalle(res.data);
      setModalDetalle(true);
    } catch(e) { toast.error("Error cargando detalle"); }
  };

  const handleSeleccionarProveedor = async (proveedor) => {
    setProveedorSeleccionado(proveedor);
    setComprobantesSeleccionados([]);
    
    try {
      const response = await axios.get(
        API_URL + "/api/erp/empresas/" + TENANT_ID + "/proveedores/" + proveedor.id + "/comprobantes-pendientes"
      );
      setComprobantesPendientes(response.data);
      
      const banco = cuentasTesoreria.find(c => c.tipo === "BANCO");
      setFormOP({
        fecha: new Date().toISOString().split('T')[0],
        medio_pago: "TRANSFERENCIA",
        cuenta_tesoreria_id: banco ? banco.id.toString() : "",
        referencia: "",
        observaciones: ""
      });
      
      setModalOP(true);
    } catch (error) {
      toast.error("Error al cargar comprobantes");
    }
  };

  const handleToggleComprobante = (comp) => {
    setComprobantesSeleccionados(prev => {
      const existe = prev.find(c => c.comprobante_id === comp.id);
      if (existe) {
        return prev.filter(c => c.comprobante_id !== comp.id);
      } else {
        return [...prev, { comprobante_id: comp.id, importe_aplicado: comp.saldo }];
      }
    });
  };

  const handleUpdateImporte = (compId, importe) => {
    setComprobantesSeleccionados(prev => 
      prev.map(c => c.comprobante_id === compId ? { ...c, importe_aplicado: parseFloat(importe) || 0 } : c)
    );
  };

  const getTotalAplicar = () => {
    return comprobantesSeleccionados.reduce((sum, c) => sum + c.importe_aplicado, 0);
  };

  const handleEmitirOP = async () => {
    if (comprobantesSeleccionados.length === 0) {
      toast.error("Seleccione al menos un comprobante");
      return;
    }
    if (!formOP.cuenta_tesoreria_id) {
      toast.error("Seleccione una cuenta de tesorería");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(API_URL + "/api/erp/empresas/" + TENANT_ID + "/ordenes-pago", {
        fecha: formOP.fecha,
        proveedor_id: proveedorSeleccionado.id,
        pagos: [{
          medio_pago: formOP.medio_pago,
          cuenta_tesoreria_id: parseInt(formOP.cuenta_tesoreria_id),
          importe: getTotalAplicar(),
          referencia: formOP.referencia
        }],
        comprobantes: comprobantesSeleccionados,
        observaciones: formOP.observaciones
      });
      
      toast.success("Orden de pago emitida correctamente");
      setModalOP(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al emitir orden de pago");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return "$ " + new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const totalPendiente = proveedoresConSaldo.reduce((sum, p) => sum + p.saldo_pendiente, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestion-pagos">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Gestión de Pagos</h1>
          <p className="text-gray-600">Pagos a proveedores y emisión de órdenes de pago</p>
        </div>
        <Button variant="outline" size="icon" onClick={fetchData}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="cao-card border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Pendiente Pago</p>
                <p className="text-2xl font-bold text-[#1E3876]">{formatCurrency(totalPendiente)}</p>
              </div>
              <DollarSign className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cao-card border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Proveedores con Saldo</p>
                <p className="text-2xl font-bold text-[#1E3876]">{proveedoresConSaldo.length}</p>
              </div>
              <Truck className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cao-card border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">OP Emitidas (mes)</p>
                <p className="text-2xl font-bold text-[#1E3876]">{ordenesPago.length}</p>
              </div>
              <FileText className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pendientes">
            <AlertCircle className="w-4 h-4 mr-2" />Pendientes de Pago
          </TabsTrigger>
          <TabsTrigger value="ordenes">
            <FileText className="w-4 h-4 mr-2" />Órdenes de Pago
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendientes" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle>Proveedores con Saldo Pendiente</CardTitle>
              <CardDescription>Seleccione un proveedor para registrar un pago</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>CUIT</TableHead>
                    <TableHead className="text-right">Saldo Pendiente</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {proveedoresConSaldo.map((proveedor) => (
                    <TableRow key={proveedor.id}>
                      <TableCell className="font-mono">{proveedor.codigo}</TableCell>
                      <TableCell className="font-medium">{proveedor.razon_social}</TableCell>
                      <TableCell>{proveedor.cuit || "-"}</TableCell>
                      <TableCell className="text-right font-bold text-red-600">
                        {formatCurrency(proveedor.saldo_pendiente)}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          onClick={() => handleSeleccionarProveedor(proveedor)}
                          className="bg-[#2853B9] hover:bg-[#1E3876]"
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          Pagar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {proveedoresConSaldo.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                        No hay proveedores con saldo pendiente
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ordenes" className="mt-4">
          <Card className="cao-card">
            <CardHeader>
              <CardTitle>Órdenes de Pago Emitidas</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ordenesPago.map((op) => (
                    <TableRow key={op.id}>
                      <TableCell className="font-mono">{op.numero_display}</TableCell>
                      <TableCell>{op.fecha}</TableCell>
                      <TableCell>{op.proveedor_nombre}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(op.total)}</TableCell>
                      <TableCell>
                        <Badge className={op.estado === "EMITIDO" ? "bg-green-100 text-green-700" : op.estado === "ANULADO" ? "bg-red-100 text-red-700" : "bg-gray-100"}>
                          {op.estado}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleVerOP(op.id)} data-testid={"btn-ver-op-"+op.id}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {op.estado !== "ANULADO" && (
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" 
                              onClick={async () => {
                                if(!window.confirm("¿Anular esta OP? Se revertirán facturas, movimientos y asientos.")) return;
                                try {
                                  await axios.put(API_URL + "/api/erp/empresas/" + TENANT_ID + "/ordenes-pago/" + op.id + "/anular");
                                  toast.success("OP anulada"); fetchData();
                                } catch(e) { toast.error(e.response?.data?.detail || "Error"); }
                              }} data-testid={"btn-anular-op-"+op.id}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {ordenesPago.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No hay órdenes de pago emitidas
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Nueva Orden de Pago */}
      <Dialog open={modalOP} onOpenChange={setModalOP}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva Orden de Pago</DialogTitle>
            {proveedorSeleccionado && (
              <DialogDescription>
                Proveedor: <strong>{proveedorSeleccionado.razon_social}</strong>
              </DialogDescription>
            )}
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Datos de la OP */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha</Label>
                <Input 
                  type="date" 
                  value={formOP.fecha}
                  onChange={(e) => setFormOP({...formOP, fecha: e.target.value})}
                />
              </div>
              <div>
                <Label>Medio de Pago</Label>
                <Select 
                  value={formOP.medio_pago} 
                  onValueChange={(v) => setFormOP({...formOP, medio_pago: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRANSFERENCIA">Transferencia</SelectItem>
                    <SelectItem value="CHEQUE">Cheque</SelectItem>
                    <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cuenta de Tesorería</Label>
                <Select 
                  value={formOP.cuenta_tesoreria_id} 
                  onValueChange={(v) => setFormOP({...formOP, cuenta_tesoreria_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentasTesoreria.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.nombre} ({c.tipo})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Referencia</Label>
                <Input 
                  value={formOP.referencia}
                  onChange={(e) => setFormOP({...formOP, referencia: e.target.value})}
                  placeholder="Nro. transferencia, cheque, etc."
                />
              </div>
            </div>

            {/* Comprobantes */}
            <div>
              <Label className="text-base font-semibold">Comprobantes a Cancelar</Label>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Comprobante</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Vencimiento</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">A Aplicar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comprobantesPendientes.map((comp) => {
                    const seleccionado = comprobantesSeleccionados.find(c => c.comprobante_id === comp.id);
                    return (
                      <TableRow key={comp.id} className={seleccionado ? "bg-green-50" : ""}>
                        <TableCell>
                          <Checkbox
                            checked={!!seleccionado}
                            onCheckedChange={() => handleToggleComprobante(comp)}
                          />
                        </TableCell>
                        <TableCell className="font-mono">{comp.comprobante_display}</TableCell>
                        <TableCell>{comp.fecha}</TableCell>
                        <TableCell>{comp.fecha_vencimiento || "-"}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(comp.saldo)}</TableCell>
                        <TableCell className="text-right">
                          {seleccionado && (
                            <Input
                              type="number"
                              step="0.01"
                              className="w-28 text-right"
                              value={seleccionado.importe_aplicado}
                              onChange={(e) => handleUpdateImporte(comp.id, e.target.value)}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {comprobantesPendientes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                        No hay comprobantes pendientes
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Resumen */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total a Pagar:</span>
                <span className="text-2xl font-bold text-red-600">{formatCurrency(getTotalAplicar())}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOP(false)}>Cancelar</Button>
            <Button 
              onClick={handleEmitirOP} 
              disabled={saving || comprobantesSeleccionados.length === 0}
              className="bg-[#2853B9] hover:bg-[#1E3876]"
            >
              {saving ? "Emitiendo..." : "Emitir Orden de Pago"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal Detalle OP */}
      <Dialog open={modalDetalle} onOpenChange={setModalDetalle}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Orden de Pago {opDetalle?.numero_display}</DialogTitle>
            <DialogDescription>Detalle de la orden de pago</DialogDescription>
          </DialogHeader>
          {opDetalle && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Fecha:</span> <strong>{opDetalle.fecha}</strong></div>
                <div><span className="text-gray-500">Estado:</span> <Badge className="bg-green-100 text-green-700">{opDetalle.estado}</Badge></div>
                <div><span className="text-gray-500">Proveedor:</span> <strong>{opDetalle.proveedor_nombre}</strong></div>
                <div><span className="text-gray-500">CUIT:</span> {opDetalle.proveedor_cuit}</div>
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2">Medios de Pago</h4>
                <Table>
                  <TableHeader><TableRow><TableHead>Medio</TableHead><TableHead>Cuenta</TableHead><TableHead>Referencia</TableHead><TableHead className="text-right">Importe</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {opDetalle.pagos.map((p,i) => (
                      <TableRow key={i}><TableCell>{p.medio_pago}</TableCell><TableCell>{p.cuenta||"-"}</TableCell><TableCell>{p.referencia||"-"}</TableCell><TableCell className="text-right font-bold">{formatCurrency(p.importe)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {opDetalle.comprobantes.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Comprobantes Aplicados</h4>
                  <Table>
                    <TableHeader><TableRow><TableHead>Comprobante</TableHead><TableHead className="text-right">Total FC</TableHead><TableHead className="text-right">Aplicado</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {opDetalle.comprobantes.map((c,i) => (
                        <TableRow key={i}><TableCell className="font-mono">{c.display}</TableCell><TableCell className="text-right">{formatCurrency(c.total_factura)}</TableCell><TableCell className="text-right font-bold">{formatCurrency(c.importe_aplicado)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <span className="font-semibold">Total OP:</span>
                <span className="text-xl font-bold text-red-600">{formatCurrency(opDetalle.total)}</span>
              </div>
              {opDetalle.asiento_id && (
                <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                  <Check className="w-4 h-4" /> Asiento contable #{opDetalle.asiento_id} generado
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionPagos;
